import { computed, onBeforeUnmount, onMounted, ref, toValue, watch } from "vue";
const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);
const PASTE_PROMPT_HINT_DELAY_MS = 400;
// A screenshot on the clipboard has no name of its own — the browser supplies a generic one
// ("image.png"), so consecutive pastes would be indistinguishable in a caller's file list.
// Stamp those; a file copied out of the OS file manager keeps its real name.
const GENERIC_CLIPBOARD_NAME = /^(image|screenshot)\.[a-z0-9]+$/i;
// Take the extension from the file's own name, else from its MIME subtype ("image/svg+xml" →
// ".svg"). A file we can read neither from keeps no extension — the upload path routes on MIME
// type, and inventing one would misdescribe the bytes.
const extensionFor = (file) => {
    const fromName = /\.([a-z0-9]+)$/i.exec(file.name || "")?.[1];
    const subtype = (file.type || "").split(";")[0].split("/")[1]?.split("+")[0];
    const extension = fromName || subtype;
    return extension ? `.${extension.toLowerCase()}` : "";
};
const nameForPastedFile = (file, index) => {
    if (file.name && !GENERIC_CLIPBOARD_NAME.test(file.name))
        return file;
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const suffix = index > 0 ? `-${index + 1}` : "";
    return new File([file], `pasted-${stamp}${suffix}${extensionFor(file)}`, { type: file.type });
};
// Chrome/Firefox fill clipboardData.files on paste; some browsers only fill items.
const clipboardFiles = (clipboard) => {
    const files = Array.from(clipboard.files);
    if (files.length > 0)
        return files;
    return Array.from(clipboard.items)
        .filter((item) => item.kind === "file")
        .map((item) => item.getAsFile())
        .filter((file) => file !== null);
};
// A paste aimed at a field the user is typing in belongs to that field, unless the clipboard
// holds nothing that field could take.
const isEditableTarget = (target) => target instanceof HTMLElement && (target.isContentEditable || ["INPUT", "TEXTAREA"].includes(target.tagName));
// Accepts the same forms as an <input accept> attribute — extensions (".pdf"), exact MIME types
// ("application/pdf") and wildcards ("image/*") — as a comma-separated string or an array, so a
// caller can pass the very string it gives its file input.
export const parseAccept = (accept) => {
    const rules = Array.isArray(accept) ? accept : String(accept || "").split(",");
    return rules.map((rule) => rule.trim().toLowerCase()).filter(Boolean);
};
export const matchesAccept = (file, rules) => {
    const name = (file.name || "").toLowerCase();
    const type = (file.type || "").toLowerCase();
    return rules.some((rule) => {
        if (rule.startsWith("."))
            return name.endsWith(rule);
        if (rule.endsWith("/*"))
            return type.startsWith(rule.slice(0, -1));
        return type === rule;
    });
};
// Content hash, so a jumpy Ctrl+V doesn't upload the same screenshot twice. Only byte-identical
// pastes match — re-snipping the same region of the screen produces different bytes, and that is
// a genuinely new file as far as we can tell. Needs a secure context (https, or localhost in dev);
// where crypto.subtle is missing we skip deduplication rather than guess from size and type, since
// wrongly dropping a file the user pasted is worse than uploading a duplicate.
const digestFor = async (file) => {
    if (!crypto?.subtle)
        return null;
    const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
    return Array.from(new Uint8Array(digest))
        .map((byte) => byte.toString(16).padStart(2, "0"))
        .join("");
};
// ".pdf, image/png" → "PDF, PNG", for the rejection message.
export const describeAccept = (rules) => {
    const labels = rules.map((rule) => (rule.startsWith(".") ? rule.slice(1) : rule.split("/").pop().replace("*", "any")).toUpperCase());
    return [...new Set(labels)].join(", ");
};
// The Async Clipboard API hands back a Blob with no name. Give it the browser's own generic one so
// nameForPastedFile stamps it like a keyboard paste.
const fileFromClipboardImage = async (item) => {
    const type = item.types.find((candidate) => candidate.startsWith("image/"));
    if (!type)
        return null;
    const blob = await item.getType(type);
    return new File([blob], `image.${type.split("/")[1].split("+")[0]}`, { type });
};
export function usePastedFiles({ onFiles, enabled = true, multiple = true, accept = "", listen = false, }) {
    const pasteHint = computed(() => `or press ${isMac ? "⌘V" : "Ctrl+V"} to paste a screenshot or copied file`);
    // Paste-specific problems only — a paste the caller never saw. Failures inside onFiles stay the
    // caller's to report, since it knows which file failed and why.
    const pasteAlert = ref(null);
    const pasting = ref(false);
    const awaitingPastePrompt = ref(false);
    const clearPasteAlert = () => {
        pasteAlert.value = null;
    };
    const raise = (type, text) => {
        pasteAlert.value = { type, text };
    };
    // Content hashes of every paste handed over so far. Per component instance: cross-session
    // deduplication would need the hash stored with the file server-side.
    const handledDigests = new Set();
    // The name each of those hashes was handed over under, so a caller that lets the user delete one
    // uploaded file can retire exactly that file's hash. Keyed on the name rather than handing digests
    // out to the caller, which would make every caller keep this bookkeeping. Two pasted files that
    // end up with the same name share an entry — the hash check still stops identical bytes being
    // re-uploaded, and it only costs the ability to forget them individually.
    const digestsByName = new Map();
    const forgetPaste = (fileName) => {
        const digest = digestsByName.get(fileName);
        if (digest)
            handledDigests.delete(digest);
        digestsByName.delete(fileName);
    };
    const forgetAll = (files) => files.forEach((file) => forgetPaste(file.name));
    // Shared tail of both entry points: filter, deduplicate, name, hand over.
    const takeFiles = async (pasted) => {
        let files = pasted;
        clearPasteAlert();
        if (!toValue(multiple))
            files = files.slice(0, 1);
        const rules = parseAccept(toValue(accept));
        if (rules.length > 0) {
            const accepted = files.filter((file) => matchesAccept(file, rules));
            const skipped = files.length - accepted.length;
            if (accepted.length === 0) {
                // Claimed the paste and dropped it, so say so — silence reads as the paste not working.
                raise("error", `That paste isn't a file type you can upload here. Supported types: ${describeAccept(rules)}.`);
                return;
            }
            if (skipped > 0) {
                raise("warning", `Skipped ${skipped} pasted ${skipped === 1 ? "file" : "files"} of an unsupported type. ` +
                    `Supported types: ${describeAccept(rules)}.`);
            }
            files = accepted;
        }
        // Drop anything already pasted this session, and repeats within this paste itself. The digests
        // are awaited once, and everything from there to the claim below is synchronous — so two
        // overlapping pastes cannot both get through: whichever resumes first claims its hashes before
        // the other's filter runs, and the other then sees them.
        const digests = await Promise.all(files.map(digestFor));
        const claimed = new Set();
        const fresh = files
            .map((file, index) => ({ file, digest: digests[index] }))
            .filter(({ digest }) => {
            if (digest && (handledDigests.has(digest) || claimed.has(digest)))
                return false;
            if (digest)
                claimed.add(digest);
            return true;
        });
        const repeats = files.length - fresh.length;
        if (fresh.length === 0) {
            raise("warning", `You already pasted ${files.length === 1 ? "that file" : "those files"}.`);
            return;
        }
        if (repeats > 0) {
            raise("warning", `Skipped ${repeats} ${repeats === 1 ? "file" : "files"} you had already pasted.`);
        }
        claimed.forEach((digest) => handledDigests.add(digest));
        // Name them now and record which hash went out under which name, so the caller can retire one
        // of them on its own later.
        const named = fresh.map(({ file }, index) => nameForPastedFile(file, index));
        named.forEach((file, index) => {
            const digest = fresh[index].digest;
            if (digest)
                digestsByName.set(file.name, digest);
        });
        try {
            // false means the caller couldn't take the files, so forget them and let the same paste be
            // retried. A partial failure rolls back the whole paste — re-pasting then re-uploads the
            // files that did land, which is what a user repeating the paste is asking for anyway.
            if ((await onFiles(named)) === false)
                forgetAll(named);
        }
        catch {
            // onFiles rejecting outright (rather than reporting per-file failures) would otherwise
            // leave the paste looking like it did nothing.
            forgetAll(named);
            raise("error", `Couldn't upload the pasted ${named.length === 1 ? "file" : "files"}. Please try again.`);
        }
    };
    const handlePaste = async (event) => {
        if (!toValue(enabled))
            return;
        const clipboard = event.clipboardData;
        if (!clipboard)
            return;
        const files = clipboardFiles(clipboard);
        if (files.length === 0)
            return;
        // Let the focused field have the paste when the clipboard holds something it can take.
        if (isEditableTarget(event.target) && clipboard.getData("text/plain"))
            return;
        event.preventDefault();
        await takeFiles(files);
    };
    // Reads what the clipboard holds right now. The Async Clipboard API only exposes text and PNG —
    // never a copied PDF or file — so this is the screenshot path; copied files need the paste event.
    // Only the most recent clipboard item is considered.
    const pasteFromClipboard = async () => {
        if (!toValue(enabled) || pasting.value)
            return;
        if (!navigator.clipboard?.read) {
            raise("error", "Clipboard access isn't available in this browser. Save the screenshot and choose it as a file instead.");
            return;
        }
        pasting.value = true;
        // Chrome answers at once; only a browser that is holding a prompt open gets past this delay.
        const promptTimer = setTimeout(() => {
            awaitingPastePrompt.value = true;
        }, PASTE_PROMPT_HINT_DELAY_MS);
        try {
            const items = await navigator.clipboard.read();
            clearTimeout(promptTimer);
            awaitingPastePrompt.value = false;
            const file = items.length > 0 ? await fileFromClipboardImage(items[items.length - 1]) : null;
            if (!file) {
                raise("error", "No screenshot on the clipboard. Copy a screen region first.");
                return;
            }
            await takeFiles([file]);
        }
        catch (error) {
            // Firefox rejects with NotAllowedError both when the site is blocked and when the user
            // dismissed its Paste prompt, so the message has to cover both.
            const blocked = error instanceof DOMException && error.name === "NotAllowedError";
            raise("error", blocked
                ? "The clipboard read was cancelled or blocked. Click Paste when the browser asks, or allow clipboard access for this site."
                : "Couldn't read the clipboard. Please try again.");
        }
        finally {
            clearTimeout(promptTimer);
            awaitingPastePrompt.value = false;
            pasting.value = false;
        }
    };
    const syncListener = (on) => on ? document.addEventListener("paste", handlePaste) : document.removeEventListener("paste", handlePaste);
    onMounted(() => {
        syncListener(Boolean(toValue(listen)));
        watch(() => Boolean(toValue(listen)), syncListener);
    });
    onBeforeUnmount(() => document.removeEventListener("paste", handlePaste));
    return { pasteFromClipboard, pasting, awaitingPastePrompt, pasteHint, pasteAlert, clearPasteAlert, forgetPaste };
}
